import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation';
import Bracket from './Bracket.js';
import StandingsSection from './StandingsSection.js';
import SeasonLeadersSection from './SeasonLeadersSection.js';
import * as utils from '../common/Utils.js';

import { getDoc, doc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext.js';

const HomepageUpdated = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const SEASON = config.currentSeason;

  const [recentGames, setRecentGames] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [seasonTeams, setSeasonTeams] = useState([]);
  const [seasonConferences, setSeasonConferences] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [seasonUpdatedAt, setSeasonUpdatedAt] = useState(null);
  const [playoffGames, setPlayoffGames] = useState([]);
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    if (!config.configLoaded || !config.showPlayoffBracket) return;

    const gamesRef = collection(db, 'games');
    const playoffGamesQuery = query(gamesRef, where('season', '==', parseInt(SEASON)), where('type', '==', 'playoff'));

    getDocs(playoffGamesQuery).then(snapshot => {
      const games = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        date: d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date),
      }));
      setPlayoffGames(games);
    });
  }, [config.configLoaded, config.showPlayoffBracket, SEASON]);

  useEffect(() => {
    if (!config.configLoaded) return;

    getDoc(doc(db, 'seasons', SEASON)).then(snap => {
      if (!snap.exists()) { setDataReady(true); return; }

      const { upcomingGames = [], teams = [], leaders = [], conferences = [], updatedAt } = snap.data();
      const today = Date.now();
      const threeDaysAgo = today - 4 * 24 * 60 * 60 * 1000;
      const oneWeekAhead = today + 7 * 24 * 60 * 60 * 1000;

      const games = upcomingGames.map(g => ({
        ...g,
        date: g.date?.toDate ? g.date.toDate() : new Date(g.date),
      }));

      setRecentGames(
        games.filter(g => g.status === 'completed' && g.date > threeDaysAgo && g.date < today)
          .sort((a, b) => b.date - a.date)
          .slice(0, 2)
      );
      setUpcomingGames(
        games.filter(g => g.status !== 'completed' && g.date > today && g.date < oneWeekAhead)
          .sort((a, b) => a.date - b.date)
          .slice(0, 4)
      );
      setSeasonTeams(teams);
      setSeasonConferences(conferences);
      setLeaders(leaders);
      setSeasonUpdatedAt(updatedAt?.toDate ? updatedAt.toDate() : null);
      setDataReady(true);
    });
  }, [config.configLoaded, SEASON]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTeamName = (id) => seasonTeams.find(t => t.id === id)?.name || id;

  const formatUpdatedAt = (date) => {
    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
    hours = hours % 12;
    if (hours === 0) hours = 12;

    // No timeZone option passed, so this resolves to the viewer's own local
    // timezone abbreviation (e.g. EDT/PDT/CDT), correctly accounting for DST.
    const timeZoneName = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value || '';

    return `Updated ${month} ${day}, ${hours}:${minutes} ${ampm} ${timeZoneName}`;
  };

  if (!dataReady) {
    return <div className="HomepageUpdated"><LoadingAnimation /></div>;
  }

  return (
    <div className="HomepageUpdated">
      <div className="league-content">
        <div className="league-homepage">

          <nav className="homepage-nav">
            {/* Only link to sections that render (same conditions as below) */}
            {upcomingGames.length > 0 && <a className="homepage-anchor-link" href="#upcoming-games">Upcoming Games</a>}
            <a className="homepage-anchor-link" href="#standings">Standings</a>
            {leaders.length > 0 && <a className="homepage-anchor-link" href="#season-leaders">Season Leaders</a>}
          </nav>
          <hr className="section-divider" />

          {/* Recent finished games */}
          <div className="homepage-recent-games">
            {recentGames.map((game, i) => {
              const awayWon = game.winner === game.awayTeamID;
              const homeWon = game.winner === game.homeTeamID;
              return (
                <div key={game.id} className="homepage-game-row">
                  {i > 0 && <hr className="content-divider" />}
                  <div className="homepage-recent-game" onClick={() => navigate(`/games/${game.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="homepage-live-row">
                      <span className="league-article-live">• LIVE</span>
                      {game.articleCreatedAt && (
                        <time className="homepage-timestamp">{utils.getTimeAgo(game.articleCreatedAt)}</time>
                      )}
                      <span className="homepage-score-line">
                        <span style={{ fontWeight: awayWon ? 700 : 400 }}>{getTeamName(game.awayTeamID)}</span>
                        {' '}
                        <span style={{ fontWeight: awayWon ? 700 : 400 }}>{game.score?.away}</span>
                        {' – '}
                        <span style={{ fontWeight: homeWon ? 700 : 400 }}>{game.score?.home}</span>
                        {' '}
                        <span style={{ fontWeight: homeWon ? 700 : 400 }}>{getTeamName(game.homeTeamID)}</span>
                      </span>
                    </div>
                    {game.articleTitle && (
                      <p className="league-game-article-title">
                        {game.articleTitle}
                        <span className="homepage-box-score">BOX SCORE →</span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <hr className="section-divider" />

          {/* Sign ups — toggled from admin config */}
          {config.showSignUps && (
            <>
              <div className="homepage-section homepage-signup">
                <p className="mini-title">Season {utils.toRomanNumeral(parseInt(SEASON))} Sign Ups</p>
                <div className="homepage-signup-row">
                  {config.signUpText && <p className="homepage-signup-text">{config.signUpText}</p>}
                  <a className="signup-btn" href="/signup">Sign Up ›</a>
                </div>
              </div>
              <hr className="section-divider" />
            </>
          )}

          {/* Playoff bracket */}
          {config.showPlayoffBracket && playoffGames.length > 0 && (
            <>
              <div className="homepage-section">
                <Bracket games={playoffGames} teams={seasonTeams} isCurrentSeason={true} showPlaceholders={false} compact></Bracket>
              </div>
              <hr className="section-divider" />
            </>
          )}

          {/* CTA — Mass times */}
          <div className="homepage-section">
            <a className="cta-title" href="https://www.gateofheavenstbrigid.org/#MassTimes" target="_blank" rel="noreferrer">
              <strong>Hungry for more?</strong> Come to Mass this Sunday at St. Brigid or Gate of Heaven ›
            </a>
          </div>
          <hr className="section-divider" />

          {/* Prayer request */}
          <div className="homepage-section-small">
            <a className="homepage-anchor-link" href="/prayer-intentions/new">Create a prayer request</a>
          </div>
          <hr className="section-divider" />

          {/* Our story */}
          <div className="homepage-section homepage-our-story">
            <div className="homepage-our-story-text">
              <p className="article-author-name">Fr. Peter Schirripa</p>
              <p className="league-article-title" style={{ cursor: 'pointer' }} onClick={() => navigate('/our-story')}>The Story of League Morning Star</p>
              <p className="article-read-time">2 min read</p>
            </div>
            <div className="homepage-our-story-logos">
              <img src="/league.png" alt="League Morning Star" className="homepage-league-logo" />
              <img src="/medal.png" alt="Medal" className="homepage-league-logo" />
            </div>
          </div>
          <hr className="section-divider" />

          {/* Upcoming games */}
          {upcomingGames.length > 0 && (
            <div id="upcoming-games" className="homepage-section">
              <div className="homepage-section-title-row">
                <p className="mini-title">Upcoming Games</p>
                {seasonUpdatedAt && <p className="homepage-updated-at">{formatUpdatedAt(seasonUpdatedAt)}</p>}
              </div>
              {upcomingGames.map((game, i) => {
                const away = seasonTeams.find(t => t.id === game.awayTeamID);
                const home = seasonTeams.find(t => t.id === game.homeTeamID);
                let hours = game.date.getHours();
                const hoursLabel = hours >= 12 ? 'PM' : 'AM';
                if (hours > 12) hours -= 12;
                if (hours === 0) hours = 12;
                return (
                  <div key={game.id} className={`league-game-container-homepage${game.status === 'cancelled' ? ' cancelled-game' : ''}`} onClick={() => navigate(`/games/${game.id}`)}>
                    <p className="league-title-skinny-small">
                      {(away?.name || 'TBD').toUpperCase()} vs. {(home?.name || 'TBD').toUpperCase()}
                      <span className="game-meta-inline"> · {utils.getLocationName(game.location)} · {game.date.toDateString()} @ {hours}:{String(game.date.getMinutes()).padStart(2, '0')} {hoursLabel}</span>
                    </p>
                    <p className="league-game-location game-meta-small">{utils.getLocationName(game.location)}</p>
                    <p className="game-meta-small">{game.date.toDateString()} @ {hours}:{String(game.date.getMinutes()).padStart(2, '0')} {hoursLabel}</p>
                  </div>
                );
              })}
              <a href="/schedule" className="homepage-anchor-link" style={{ display: 'block', marginTop: 10 }}>View All Games</a>
            </div>
          )}
          {upcomingGames.length > 0 && <hr className="section-divider" />}

          {/* Standings */}
          <StandingsSection teams={seasonTeams} conferences={seasonConferences} />
          {seasonTeams.length > 0 && <hr className="section-divider" />}

          {/* Season leaders — top 10 per stat */}
          <SeasonLeadersSection leaders={leaders} />
          {leaders.length > 0 && <hr className="section-divider" />}

          <div className="homepage-section">
            <p className="league-article-title" style={{ cursor: 'pointer' }} onClick={() => navigate(`/seasons/${utils.getLastSeason(SEASON)}`)}>Results from Season {utils.getLastSeason(SEASON)}</p>
            <p className="article-read-time">See final standings and playoff bracket from last season</p>
          </div>
          <hr className="section-divider" />

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HomepageUpdated;
